<script lang="ts" setup>
import { computed } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import SwipeBackIndicator from '@/components/Base/SwipeBackIndicator/index.vue'
import PullToRefreshIndicator from '@/components/Base/PullToRefreshIndicator/index.vue'
import { useSwipeBackGesture } from '@/composables/useSwipeBackGesture'
import { usePinchZoomGuard } from '@/composables/usePinchZoomGuard'
import { usePullToRefresh } from '@/composables/usePullToRefresh'
import { useIdleLogout } from '@/composables/useIdleLogout'
import { usePasswordExpiryPrompt } from '@/composables/usePasswordExpiryPrompt'
import { useRouteScrollReset } from '@/composables/useRouteScrollReset'
import { isNestedScrollPrevented } from '@/constants/scrollable-overlay.const'
import { useLocale } from '@/composables/useLocale'
import zhTW from 'ant-design-vue/es/locale/zh_TW'
import enUS from 'ant-design-vue/es/locale/en_US'

// ant-design-vue 內建文字（分頁的「X 條/頁」、日期選擇器、空資料…）預設是英文，
// 之前沒掛 ConfigProvider，所以不管瀏覽器語系都顯示英文。語系判斷與 i18n 同一套
const antdLocale = useLocale() === 'tw' ? zhTW : enUS

const route = useRoute()
const routeClassName = computed(() =>
  typeof route.name === 'string' || typeof route.name === 'number' ? String(route.name) : ''
)

// 下拉選單內的捲動不要交給 Lenis 處理，否則滑選單時外層頁面會跟著動。
//
// 光靠 overscroll-behavior 擋不住：ant-design-vue 的選單是虛擬捲動（rc-virtual-list
// 用 JS + transform 自己捲），容器的 overflow-y 是 hidden，而 Lenis 的巢狀捲動偵測
// 第一關就是 ['auto','overlay','scroll'].includes(overflowY)，hidden 直接出局，
// 於是它認定這不是捲動區域而接管整頁。改用 Lenis 官方的 prevent 明確排除這些容器。
const preventLenisScroll = (node: HTMLElement) => isNestedScrollPrevented(node)

const { dragX, isDragging } = useSwipeBackGesture()
const { pullDistance, isDragging: isPulling } = usePullToRefresh()
usePinchZoomGuard()
useIdleLogout()
usePasswordExpiryPrompt()
useRouteScrollReset()
</script>

<template>
  <a-config-provider :locale="antdLocale">
    <vue-lenis
      root
      :options="{
        autoRaf: true,
        smoothWheel: false,
        syncTouch: true,
        allowNestedScroll: true,
        autoToggle: true,
        prevent: preventLenisScroll
      }"
    >
      <div id="operate-modal-portal"></div>
      <SwipeBackIndicator />
      <PullToRefreshIndicator />

      <!-- 平常不能掛 transform：非 none 的 transform 會讓底下 position: fixed 的元素（例如側邊欄）
         改成相對這層定位，只在真的拖曳中（pullDistance > 0）才加上；跟 .page-slide-back 的
         X 軸 transform 是獨立的兩層，不合併，避免互相干擾 -->
      <div
        class="pull-refresh-content"
        :class="{ 'pull-refresh-content--dragging': isPulling }"
        :style="pullDistance > 0 ? { transform: `translateY(${pullDistance}px)` } : undefined"
      >
        <!-- 平常不能掛 transform：非 none 的 transform 會讓底下 position: fixed 的元素（例如側邊欄）
           改成相對這層定位，只在真的拖曳中（dragX > 0）才加上 -->
        <div
          class="page-slide-back"
          :class="{ 'page-slide-back--dragging': isDragging }"
          :style="dragX > 0 ? { transform: `translateX(${dragX}px)` } : undefined"
        >
          <component
            class="container"
            :is="route.meta.layoutComponent || 'div'"
          >
            <main
              class="main-page"
              :class="routeClassName"
            >
              <RouterView />
            </main>
          </component>
        </div>
      </div>
    </vue-lenis>
  </a-config-provider>
</template>

<style lang="scss">
.page-slide-back {
  box-shadow: -4px 0 12px rgba(0, 0, 0, 0.15);
  transition: transform 0.25s ease; // 需與 useSwipeBackGesture 的 COMPLETE_TRANSITION_MS 一致

  &--dragging {
    transition: none; // 拖曳中即時跟手，不要有過渡延遲
  }
}

.pull-refresh-content {
  transition: transform 0.25s ease;

  &--dragging {
    transition: none; // 拖曳中即時跟手，不要有過渡延遲
  }
}

.container {
  position: relative;
  margin: 0 auto;
}

.layout-blank {
  .main-page {
    min-height: 100vh;
  }
}
.main-page {
  min-height: calc(100vh - $--header-height);
  padding-inline: $--page-padding-x;
  padding-block: 0 $--page-padding-bottom;
  background-color: $--page-bg-color;

  &.Login,
  &.SignUp {
    min-height: 100vh;
  }

  @include media-breakpoint-down(md) {
    padding-inline: $--page-padding-x-mobile;
  }
}

.group-tag {
  padding: 0.25rem 0.5rem;
  background-color: $--color-primary;
  color: $--color-white;
  border-radius: $--border-radius-middle;
}

/** Ant Design Form */
.ant-form-item {
  margin-bottom: 1rem;

  &:last-of-type {
    margin-bottom: 0;
  }
}

/** Ant Design Button */
.ant-btn {
  @include base-transition;
  height: auto;
  padding-block: 0.75rem;
  border: none;

  &.ant-btn-default {
    background-color: $--color-default;
    color: $--color-white;
    border: none;

    &:hover {
      background-color: $--color-default--hover;
      color: $--color-white;
      box-shadow: $--box-shadow-light;
    }

    &[disabled] {
      background-color: $--color-default--disabled;
    }
  }

  &.ant-btn-primary {
    background-color: $--color-primary;
    color: $--color-white;

    &:hover {
      background-color: $--color-primary--hover;
      box-shadow: $--box-shadow-light;
    }

    &[disabled] {
      background-color: $--color-primary--disabled;
    }

    &.ant-btn-background-ghost {
      background-color: $--color-white;
      color: $--color-primary;
      border: 1px solid $--color-primary;

      &:not([disabled]):hover {
        background-color: $--color-primary;
        color: $--color-white;
      }

      &[disabled] {
        background-color: $--color-gray-300;
        color: $--color-gray-600;
        border-color: $--color-gray-400;
        box-shadow: none;
      }
    }
  }

  &.ant-btn-secondary {
    padding-block: 0.5rem;
    background-color: $--color-secondary;
    color: $--color-white;

    &.ant-btn-background-ghost {
      background-color: $--color-white;
      color: $--color-secondary;
      border: 1px solid $--color-secondary;

      &:hover {
        background-color: $--color-secondary;
        color: $--color-white;
      }
    }
  }

  &.ant-btn-success {
    &.ant-btn-background-ghost {
      background-color: $--color-white;
      color: $--color-success;
      border: 1px solid $--color-success;

      &:hover {
        background-color: $--color-success;
        color: $--color-white;
      }
    }
  }

  &.ant-btn-delete {
    &.ant-btn-background-ghost {
      background-color: $--color-white;
      color: $--color-danger;
      border: 1px solid $--color-danger;

      &:hover {
        background-color: $--color-danger;
        color: $--color-white;
      }
    }
  }

  &.ant-btn-third-party {
    @include base-transition;
    display: flex;
    align-items: center;
    background-color: $--color-white;
    border: 1px solid $--color-gray-400;
    color: $--color-primary;
    padding: 0;

    &:hover {
      box-shadow: $--box-shadow-light;
    }
  }

  &.ant-btn-outlined {
    @include base-transition;
    height: auto;
    padding-block: 0.75rem;
    background-color: $--color-white;
    color: $--color-primary;
    border: 1px solid $--color-primary;

    &:hover {
      color: $--color-white;
      background-color: $--color-primary;
      box-shadow: $--box-shadow-light;
    }

    &[disabled] {
      background-color: $--color-primary--disabled;
    }
  }
}

/** Ant Design Float Button */
.ant-float-btn {
  &-primary {
    .ant-float-btn-body {
      @include base-transition;
      background-color: $--color-danger;

      &:hover {
        background-color: darken($--color-danger, 10%);
      }
    }
  }
}

/** Ant Design Modal */
.ant-modal {
  & .ant-modal-content {
    padding: 0;
    padding-bottom: 0.75rem;
    background-color: $--background-color-base;
  }

  & .ant-modal-header {
    padding: 0;
    margin: 0;
    overflow: hidden;

    .modal-header {
      padding: 0.75rem;

      &-default {
        background-color: $--background-color-base;

        .modal-title {
          color: $--color-primary;
        }
      }

      &-primary {
        background-color: $--color-primary;

        .modal-title {
          color: $--color-white;
        }
      }
    }
  }

  & .ant-modal-body {
    margin-inline: 0.75rem;
    margin-block: 0.5rem;
  }

  & .ant-modal-footer {
    margin-inline: 0.75rem;
  }

  & .ant-modal-close {
    top: 0.75rem;
    inset-inline-end: 0.75rem;
  }

  & .ant-modal-close-x {
    display: flex;
    align-items: center;
    justify-content: center;
    color: $--color-primary;
  }

  &.primary {
    .ant-modal-close-x {
      color: $--color-white;
    }
  }
}

/** Ant Design Result */
.ant-result {
  padding: 0;
  max-width: 100%; // 確保容器不超出父級容器
  width: 100%;

  &.status-notification {
    display: grid;
    grid-template-areas: 'icon title' 'extra extra';
    grid-template-columns: auto 1fr;
    gap: 1rem;
  }
  &.no-icon {
    .ant-result-icon {
      display: none;
    }
  }

  .ant-result-icon {
    grid-area: icon;
    margin-bottom: 0px;
    display: flex;
    flex-direction: column;
    justify-content: center;

    .anticon {
      width: 100%;

      @include media-breakpoint-down(md) {
        font-size: 50px;
      }
    }
  }

  .ant-result-title {
    grid-area: title;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-items: center;
    gap: 0.5rem;

    .title {
      text-align: left;
      color: $--color-primary;
    }
    .sub-title {
      text-align: left;
      font-size: 1rem;
      color: $--color-gray-600;
      flex: 1;
    }
  }

  .ant-result-extra {
    grid-area: extra;
    margin-top: 0;

    .notification-button {
      width: 100%;

      &.error {
        background-color: $--color-error;
      }

      &.success {
        background-color: $--color-success;
      }
    }
  }
}

/** Ant Design Empty */
.ant-empty {
  margin-block: 0.5rem;
}

/** Ant Design Notification */
.ant-notification {
  & .ant-notification-notice-close {
    display: none;
  }
}

/** Ant Design Radio */
.ant-radio-wrapper {
  color: $--color-primary;

  &:hover {
    .ant-radio-inner {
      border-color: $--color-primary;
    }
  }
}

.ant-radio-input:checked + .ant-radio-inner {
  border-color: $--color-gray-500;
  background-color: $--color-white;

  &:after {
    width: $--radio-checkbox-size;
    height: $--radio-checkbox-size;
    background-color: $--color-primary;
    margin-block-start: calc($--radio-checkbox-size / 2) * -1;
    margin-inline-start: calc($--radio-checkbox-size / 2) * -1;
  }
}

.ant-radio-input:checked:hover + .ant-radio-inner {
  border-color: $--color-primary;
}

.ant-modal-root .ant-modal-wrap,
.ant-select-dropdown {
  z-index: $--modal-z-index;
}

// iPhone Safari 會放大字級過小的聚焦輸入框，包含 Select 內透明的搜尋 input。
@media (any-pointer: coarse) {
  .ant-select .ant-select-selector .ant-select-selection-search-input {
    font-size: max(16px, 1em);
  }
}

// 下拉選單內捲動時不要把捲動傳遞到外層頁面。
//
// ant-design-vue 的選單捲動容器（vc-virtual-list 的 holder）只設了 overflow-y: auto，
// 沒有設 overscroll-behavior，於是是預設的 auto。本專案以 Lenis（root + syncTouch）
// 接管整頁捲動，它判斷巢狀捲動時有這一條：
//
//   if (!hasOverscrollBehavior && (scroll >= maxScroll || scroll <= 0)) return true
//
// hasOverscrollBehavior 指的是「overscroll-behavior 為 auto」。所以在預設值之下，
// 選單一旦捲到頂或底，Lenis 就會接手把外層頁面一起捲走。
// 設成 contain 後，選單在邊界也不會把手勢交棒出去（同時也擋掉瀏覽器原生的 scroll chaining）。
.rc-virtual-list-holder,
.ant-dropdown-menu,
.ant-select-dropdown {
  overscroll-behavior: contain;
}

// iOS Safari 對字級小於 16px 的輸入框，聚焦時會強制放大整頁，之後不保證自動縮回；
// 密碼管理器自動填入也會觸發。ant 的預設 fontSize 是 14，所以全站的表單控制項都中。
//
// 放大後還會連帶讓列表捲動出問題（手勢中途在 Lenis 與原生之間換手），把字級提到 16px
// 是從源頭讓放大不要發生，而不是事後補救。
//
// 用 pointer: coarse 而非寬度：這是觸控裝置的行為，iPad 直向 768px 一樣會放大；
// 桌機（pointer: fine）維持原本的 14px，視覺不變。
//
// !important 是必要的：ant 的 .ant-input 等 class 權重高於裸元素選擇器，而 ant-design-vue
// v4 的樣式是執行時動態注入，我們的靜態樣式表誰先誰後不保證，光疊權重會變成賭注。
// iOS 只看「聚焦元素」本身的字級，所以對準真正可聚焦的三種控制項即可。
@media (pointer: coarse) {
  input,
  textarea,
  select {
    font-size: 16px !important;
  }
}
</style>
